import React, { useEffect, useState } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import yaml from 'react-syntax-highlighter/dist/esm/languages/hljs/yaml';
import { coreThemeDark, coreThemeLight } from './coreTheme';

// Register languages
SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('yaml', yaml);

export interface CodeViewerProps {
  /** The code to display */
  code: string;
  /** Programming language for syntax highlighting */
  language?: string;
  /** Whether to show line numbers */
  showLineNumbers?: boolean;
  /** Theme mode */
  theme?: 'light' | 'dark' | 'auto';
  /** Maximum height of the code viewer */
  maxHeight?: string | number;
  /** Whether to show a copy button */
  showCopyButton?: boolean;
  /** Whether to wrap long lines */
  wrapLongLines?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

// Language mapping for compatibility
const languageMap: Record<string, string> = {
  'JavaScript': 'javascript',
  'JSON': 'json',
  'XML': 'xml',
  'Python': 'python',
  'cURL': 'bash',
  'Shell': 'bash',
  'HTTPie': 'bash',
  'YAML': 'yaml',
  'javascript': 'javascript',
  'json': 'json',
  'xml': 'xml',
  'python': 'python',
  'bash': 'bash',
  'yaml': 'yaml'
};

export function CodeViewer({ 
  code, 
  language = 'javascript', 
  showLineNumbers = true,
  theme = 'auto',
  maxHeight,
  showCopyButton = false,
  wrapLongLines = false,
  className = '',
  style 
}: CodeViewerProps) {
  const [isDark, setIsDark] = useState(false);
  
  // Detect dark mode
  useEffect(() => {
    if (theme === 'auto') {
      const checkDarkMode = () => {
        const darkMode = document.documentElement.getAttribute('data-theme') === 'dark';
        setIsDark(darkMode);
      };

      checkDarkMode();
      
      const observer = new MutationObserver(checkDarkMode);
      observer.observe(document.documentElement, { 
        attributes: true, 
        attributeFilter: ['data-theme'] 
      });
      
      return () => observer.disconnect();
    } else {
      setIsDark(theme === 'dark');
    }
  }, [theme]);

  const mappedLanguage = languageMap[language] || 'javascript';
  const effectiveTheme = isDark ? coreThemeDark : coreThemeLight;

  return (
    <div 
      className={`core-code-viewer ${className}`}
      style={{
        ...style,
        borderRadius: '0',
        overflow: wrapLongLines ? 'hidden' : 'auto',
        backgroundColor: 'transparent',
        position: 'relative',
        maxHeight: maxHeight
      }}
      data-code-snippet="true"
    >
      <style>
        {`
          .core-code-viewer pre {
            background-color: transparent !important;
          }
          .core-code-viewer code {
            background-color: transparent !important;
          }
          .core-code-viewer .hljs {
            background-color: transparent !important;
          }
        `}
      </style>
      <SyntaxHighlighter
        language={mappedLanguage}
        style={effectiveTheme}
        showLineNumbers={showLineNumbers}
        wrapLongLines={wrapLongLines}
        customStyle={{
          margin: 0,
          padding: '0.75rem',
          fontSize: '0.75rem',
          lineHeight: '1.5',
          backgroundColor: 'transparent',
          overflowX: wrapLongLines ? 'hidden' : 'auto'
        }}
        lineNumberStyle={{
          color: '#6e7681',
          opacity: 0.5,
          fontSize: '0.75rem',
          minWidth: '3rem',
          paddingRight: '1.8rem',
          textAlign: 'right',
          userSelect: 'none'
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

export default CodeViewer;
